import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Card, CardBody } from 'reactstrap';
import { CORE_VALUES } from './Form/formConstants';

interface FormData {
  name: string;
  dateOfIncident: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  descriptionOfOneOnOne: string;
  coreValues: string[];
  meetingGoal: string;
  privateNotes: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface OneOnOneSummaryProps {
  isOpen: boolean;
  toggle: () => void;
  submittedForms: Map<string, FormData>;
  policyViolations: PolicyViolation[];
  onBackToEdit: () => void;
  onSubmit: (approved: boolean) => void;
  isSubmitting: boolean;
}

const OneOnOneSummary = ({
  isOpen,
  toggle,
  submittedForms,
  policyViolations,
  onBackToEdit,
  onSubmit,
  isSubmitting,
}: OneOnOneSummaryProps) => {
  const getPolicyViolationNames = (ids: string[]) => {
    return ids
      .map((id) => {
        const policy = policyViolations.find((p) => p.id === id);
        return policy ? policy.name : 'N/A';
      })
      .join(', ');
  };

  const getCoreValueLabels = (values: string[]) => {
    return values
      .map((valueId) => {
        const coreValue = CORE_VALUES.find((cv) => cv.id === valueId);
        return coreValue ? coreValue.label : valueId;
      })
      .join(', ');
  };

  const entries = Array.from(submittedForms.entries());

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" style={{ maxWidth: '1200px' }}>
      <ModalHeader toggle={toggle}>Review One on One Forms</ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <div className="mb-3">
          <p className="text-muted">
            <i className="fas fa-info-circle mr-2" />
            Please review the information below before submitting. You can go back to edit if needed.
          </p>
        </div>

        {entries.map(([userId, formData], index) => (
          <Card key={userId} className="mb-3">
            <CardBody>
              <h5 className="text-primary mb-3">
                {index + 1}. {formData.name}
              </h5>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Date of Incident:</strong>
                  <p>{new Date(formData.dateOfIncident).toLocaleDateString()}</p>
                </Col>
                <Col md={6}>
                  <strong>Position:</strong>
                  <p>{formData.position || 'N/A'}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Department:</strong>
                  <p>{formData.department || 'N/A'}</p>
                </Col>
                <Col md={6}>
                  <strong>Supervisor:</strong>
                  <p>{formData.supervisor || 'N/A'}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <strong>Policy Violations:</strong>
                  <p>{getPolicyViolationNames(formData.policyViolationIds)}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <strong>Description of One On One:</strong>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{formData.descriptionOfOneOnOne}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <strong>Core Values:</strong>
                  <p>{getCoreValueLabels(formData.coreValues) || 'N/A'}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <strong>Meeting Goal:</strong>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{formData.meetingGoal}</p>
                </Col>
              </Row>

              {formData.privateNotes && (
                <Row className="mb-3">
                  <Col md={12}>
                    <strong className="text-warning">
                      <i className="fas fa-lock mr-1" />
                      Private Notes:
                    </strong>
                    <p style={{ whiteSpace: 'pre-wrap' }} className="text-muted">
                      {formData.privateNotes}
                    </p>
                    <small className="text-muted">Only visible to Director and Upper Management</small>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        ))}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button color="outline-primary" onClick={onBackToEdit} disabled={isSubmitting}>
          <i className="fas fa-arrow-left mr-2" />
          Back to Edit
        </Button>
        <div className="ml-auto">
          <Button color="warning" onClick={() => onSubmit(false)} disabled={isSubmitting} className="mr-2">
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2" />
                Save as Draft
              </>
            )}
          </Button>
          <Button color="primary" onClick={() => onSubmit(true)} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane mr-2" />
                Submit {entries.length > 1 ? `(${entries.length})` : ''}
              </>
            )}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default OneOnOneSummary;
